import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { SelectItem } from 'ui/common/select/types';
import { Paginator } from 'ui/common/paginator';
import { maxOptionsPerPageLimit } from 'ui/common/select/constants';

export interface OptionsPaginatorProps {
  options: SelectItem[];
  children: (paginatedOptions: SelectItem[]) => JSX.Element;
  maxOptionsPerPage?: number;
  minimumOptionsNeededBeforePagination?: number;
  textPageOf?: string;
  selectedValues?: (string | number)[];
}

export const OptionsPaginator = ({ 
    options, 
    children, 
    selectedValues,
    maxOptionsPerPage = maxOptionsPerPageLimit,
    minimumOptionsNeededBeforePagination = 48,
    textPageOf = ''
  }: OptionsPaginatorProps) => {

  const [ currentPage, setCurrentPage ] = useState(1);

  const numberOfPages = options ? Math.ceil(options.length / maxOptionsPerPage) : 1;
  const shouldPaginate = numberOfPages > 1 && options && options.length > minimumOptionsNeededBeforePagination;
  const startIndex = (currentPage - 1) * maxOptionsPerPage;
  
  // Reset to first page (or page with selected option) if we receive new options
  useEffect(() => {
    const optionIndex = options?.findIndex(item => selectedValues?.some(selected => selected === item.value));
    if (selectedValues?.length && optionIndex >= 0) {
      const page = Math.ceil((optionIndex + 1) / maxOptionsPerPage);
      setCurrentPage(page);
    }
    else {
      setCurrentPage(1);
    }
  }, [maxOptionsPerPage, minimumOptionsNeededBeforePagination, options]);
  
  const currentPageOptions = shouldPaginate ?
    options && options.slice(startIndex, startIndex + maxOptionsPerPage) :
    options;
  
  const first = useCallback(() => setCurrentPage(1), [options]);
  const previous = useCallback(() => setCurrentPage(currentPage - 1), [currentPage, options]);
  const next = useCallback(() => setCurrentPage(currentPage + 1), [currentPage, options]);
  const last = useCallback(() => setCurrentPage(numberOfPages), [maxOptionsPerPage, numberOfPages, options]);
  
  return (
    <>
      {children(currentPageOptions)}
      {shouldPaginate ? (
          <Paginator 
            {...{ first, previous, next, last, textPageOf, currentPage, numberOfPages }}
          />
      ) : null}
    </>
  );
};
